// src/rms/utils/rmsDashboardViewState.js
//
// Session-scoped persistence keys for the RMS provider dashboard's *view
// configuration* — which sub-stage tab (e.g. "Pending 1st Review") and which
// pathway tab (ADHD / autism) the clinician is looking at — plus helpers to
// validate a restored sub-stage and to clear the namespace.
//
// The actual persistence is done by the generic useSessionState hook
// (src/hooks/useSessionState.js); this module only owns the RMS dashboard key
// namespace, mirroring the clinician platform's src/utils/dashboardViewState.js.
//
// WHY THIS EXISTS
// ----------------
// The provider dashboard is a route component, so opening a patient and
// navigating back unmounts it and resets its local state — dropping the
// clinician back onto the default "Incomplete Referral Forms" tab every time,
// even though they came from "Pending 1st Review". Persisting the selected tab
// for the browser-tab session keeps them where they were.
//
// NOT PII (contrast with the clinician platform)
// -----------------------------------------------
// dashboardViewState.js clears itself on sign-out because its filters can hold
// a patient's name. The values here are only tab identifiers
// ('pending-1st-review', 'adhd') — no patient data — so cross-user leakage is
// not a concern. They are still cleared on the provider sign-out for tidiness,
// and sessionStorage is scoped to the tab and cleared when it closes.

// Bump the version segment if a persisted shape changes incompatibly, so stale
// entries from older builds are ignored rather than mis-read.
const PREFIX = 'td.rms.dash.v1.';

export const RMS_DASHBOARD_KEYS = {
  subStage: PREFIX + 'subStage',
  pathway: PREFIX + 'pathway',
};

/**
 * Coerce a restored sub-stage id to a known one, falling back when it is absent
 * or no longer valid (e.g. a persisted id from a build where the stage existed).
 * Keeps the dashboard from restoring to a tab that renders an empty table.
 *
 * @param {string|null|undefined} id - the persisted sub-stage id
 * @param {string[]} validIds - the sub-stage ids the current build knows about
 * @param {string} fallback - the default sub-stage id
 * @returns {string}
 */
export function coerceSubStage(id, validIds, fallback) {
  return Array.isArray(validIds) && validIds.includes(id) ? id : fallback;
}

/**
 * Remove every persisted RMS dashboard view-config entry. Called on provider
 * sign-out so the next user on the same browser tab starts from the default
 * view (and so a stale pathway tab never carries across a session change).
 */
export function clearRmsDashboardViewState() {
  try {
    Object.values(RMS_DASHBOARD_KEYS).forEach((key) => sessionStorage.removeItem(key));
  } catch (_) {
    // sessionStorage can throw (private mode, disabled storage, SSR); a failed
    // clear is harmless because the values hold no patient data.
  }
}
